import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  getLeaseActionStateAction,
  recordLeaseActionChoiceAction,
} from "../../redux/actions/noticeToVacate";
import FullPageLayout from "../../layout/FullPageLayout";
import thankyouSVG from "./../../assets/img/svg/circle-check-solid.svg";

const LeaseAction = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useHistory();
  const { zzid_LESE, zzid_RINC, zzid_PEPL, option } = useParams();
  const leaseAction = useSelector(
    (state) => state?.NOTICE_VACATE_REDUCER?.leaseAction
  );
  const recordingLoading = useSelector(
    (state) => state?.NOTICE_VACATE_REDUCER?.loading
  );

  const [stateLoading, setStateLoading] = useState(true);
  const [brokenLink, setBrokenLink] = useState(false);
  const [recordedResult, setRecordedResult] = useState(null);

  useEffect(() => {
    setStateLoading(true);
    setBrokenLink(false);
    Promise.resolve(
      dispatch(
        getLeaseActionStateAction(zzid_LESE, zzid_RINC, zzid_PEPL, () => {
          setBrokenLink(true);
        })
      )
    ).finally(() => {
      setStateLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (stateLoading || brokenLink) return;
    if (!leaseAction) return;
    if (leaseAction.existingChoice) return;
    if (recordedResult) return;
    if (option === "2" || option === "3") {
      dispatch(
        recordLeaseActionChoiceAction(
          zzid_LESE,
          zzid_RINC,
          zzid_PEPL,
          option,
          (data) => setRecordedResult(data)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateLoading, brokenLink, leaseAction, option]);

  const handleConfirmVacate = () => {
    dispatch(
      recordLeaseActionChoiceAction(
        zzid_LESE,
        zzid_RINC,
        zzid_PEPL,
        option,
        (data) => {
          setRecordedResult(data);
          if (data?.redirectTo) {
            navigate.push(data.redirectTo);
          }
        }
      )
    );
  };

  const renderBrokenLink = () => (
    <div>
      <h4>{t("modal.notice.no_records_found")}</h4>
      <p>{t("modal.notice.this_could_happen_if")}</p>
      <ul>
        <li>{t("modal.notice.application_has_finalise")}</li>
        <li>{t("modal.notice.the_link_is_broken_or_expired")}</li>
      </ul>
    </div>
  );

  const renderLeaseInfo = () => {
    const { lease } = leaseAction;
    return (
      <div className="text-center mb-3">
        {lease.zzIDFriendly && (
          <p style={{ marginBottom: 4 }}>{lease.zzIDFriendly}</p>
        )}
        <p style={{ marginBottom: 4 }}>
          {[lease.address.address01, lease.address.address02]
            .filter(Boolean)
            .join(", ")}
        </p>
        <p style={{ marginBottom: 4 }}>
          {[lease.address.city, lease.address.state, lease.address.postalCode]
            .filter(Boolean)
            .join(", ")}
        </p>
        {lease.unitNumber && (
          <p style={{ marginBottom: 4 }}>
            {t("leaseAction.unit")}: {lease.unitNumber}
          </p>
        )}
      </div>
    );
  };

  const renderExistingChoice = () => {
    const { existingChoice } = leaseAction;
    const tsDisplay = new Date(existingChoice.decisionTs).toLocaleString(
      "en-US",
      { timeZone: "America/New_York" }
    );
    if (existingChoice.decisionEmail) {
      return (
        <p>
          {existingChoice.decisionEmail} {t("leaseAction.alreadyChose")}{" "}
          <strong>{existingChoice.decision}</strong> {t("leaseAction.on")}{" "}
          {tsDisplay}
        </p>
      );
    }
    return (
      <p>
        {t("leaseAction.tenantAlreadyChose")}{" "}
        <strong>{existingChoice.decision}</strong> {t("leaseAction.on")}{" "}
        {tsDisplay}
      </p>
    );
  };

  const renderRecorded = () => {
    if (option === "1") {
      return <p>{t("leaseAction.redirectingToVacate")}</p>;
    }
    return <p>{t("leaseAction.thankYouRenew")}</p>;
  };

  const renderOption1Confirm = () => (
    <div className="text-center">
      <p>{t("leaseAction.confirmVacatePrompt")}</p>
      <button
        type="button"
        className="btn btn-primary mt-3"
        onClick={handleConfirmVacate}
        disabled={recordingLoading}
      >
        {recordingLoading
          ? t("leaseAction.processing")
          : t("leaseAction.continueToVacate")}
      </button>
    </div>
  );

  const renderBody = () => {
    if (stateLoading) {
      return (
        <>
          <span className="spinner-grow text-primary"></span>
          <p>Please wait...</p>
        </>
      );
    }
    if (brokenLink || !leaseAction) {
      return renderBrokenLink();
    }
    if (leaseAction.existingChoice) {
      return (
        <>
          {renderLeaseInfo()}
          {renderExistingChoice()}
        </>
      );
    }
    if (recordedResult) {
      return (
        <>
          {renderLeaseInfo()}
          <img src={thankyouSVG} alt="" width={140} />
          {renderRecorded()}
        </>
      );
    }
    if (option === "1") {
      return (
        <>
          {renderLeaseInfo()}
          {renderOption1Confirm()}
        </>
      );
    }
    return (
      <>
        {renderLeaseInfo()}
        <span className="spinner-grow text-primary"></span>
        <p>{t("leaseAction.recording")}</p>
      </>
    );
  };

  return (
    <FullPageLayout>
      <div
        className="d-flex justify-content-center align-items-center flex-column"
        style={{ minHeight: "75vh" }}
      >
        {renderBody()}
      </div>
    </FullPageLayout>
  );
};

export default LeaseAction;
